/* Dependencies */
import styled from 'styled-components';

/* Components */
import { Button, Texter } from 'lego-dist';

/* Icons */
import { ReactComponent as EditIcon } from 'images/icons/editRound.svg';
import { ReactComponent as Cancel } from 'images/icons/cancel.svg';
import { ReactComponent as Save } from 'images/icons/save.svg';

interface CenterInfoProps {
  disable?: boolean;
}

export const CenterInfo = styled.div.attrs(() => ({
  className: 'CenterInfo',
}))<CenterInfoProps>`
  display: flex;
  align-items: center;
  margin: 10px 0 0 16px;
  pointer-events: ${({ disable }) => disable ? 'none' : 'initial'};

  & form {
    display: flex;
    align-items: center;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    margin: 0;
  }
`;

interface CenterImageContainerProps {
  disabled?: boolean;
}

export const CenterImage = styled.div.attrs(() => ({
  className: 'CenterImage',
}))<CenterImageContainerProps>`
  position: relative;

  .legoPhotoUploaderContainer {
    .legoPhotoUploaderDropZone {
      .legoAvatarContainer {
        ${({ disabled = false }) => `
          ${disabled ? `
            pointer-events: none;
          ` : ''}
        `}
        .legoAvatarSkeleton {
          padding: 0;

          .legoAvatarPhoto {
            height: 52px;
            width: 52px;
            :after {
              color: #ffffff;
              background-color: #32AFE0;
            }
          }
        }
      }
    }
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    .legoPhotoUploaderContainer {
      .legoPhotoUploaderDropZone {
        .legoAvatarContainer {
          .legoAvatarSkeleton {
            .legoAvatarPhoto {
              height: 80px;
              width: 80px;
            }
          }
        }
      }
    }
  }
`;

export const CenterName = styled.div.attrs(() => ({
  className: 'CenterName',
}))`
  word-break: break-all;
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  font-weight: bold;
  margin: auto 10px auto 16px;
  max-width: 288px;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 18px;
  }
`;

export const Overlay = styled.div.attrs(() => ({ className: 'DarkOverlay' }))`
  background-color: ${({ theme }) => theme.colors.blackAlpha};

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  height: 100vh;
  width: 100vw;
  z-index: 10000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const CenterEditWrapperMobile = styled.div.attrs(() => ({
  className: 'CenterEditWrapperMobile',
}))`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  background-color: ${({ theme }) => theme.colors.white};
  padding: 40px 16px 16px 16px;
  width: 100%;
  z-index: 11000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const CenterEditWrapperDesktop = styled.div.attrs(() => ({
  className: 'CenterEditWrapperDesktop',
}))`
  display: none;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: flex;
    align-items: center;
    position: relative;
  }
`;

export const CenterNameCharCount = styled.div.attrs(() => ({
  className: 'CenterNameCharCount',
}))`
  color: ${({ theme }) => theme.colors.shadeMedium};
  font-size: 14px;
  position: absolute;
  top: 18px;
  right: 16px;

  ${({ theme }) => theme.breakPoints.tablet} {
    top: -20px;
    right: 74px;
  }
`;

export const CenterEditName = styled(Texter).attrs(() => ({
  className: 'CenterEditName',
}))`
  > input {
    height: 40px;
    width: 100%;

    border: 1px solid ${({ theme }) => theme.colors.shadeMedium};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.shadeMediumDark} !important;
    font-size: 16px !important;
    font-weight: normal;
    line-height: 16px;
    margin-bottom: 25px;
    padding: 0 0 0 12px;

    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.physicalLight30};
    }
  }

  & .texter-error {
    color: #ef4b48;
    font-size: 14px;
    font-weight: normal;

    position: absolute;
    top: 45px;
    left: 0;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    > input {
      margin: auto 10px auto 16px;
      width: 288px;
    }

    & .texter-error {
      left: 16px;
    }
  }
`;

export const EditBtn = styled(EditIcon).attrs(() => ({
  className: 'CenterNameEditButton',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }
`;

export const EditBtnImg = styled(EditIcon).attrs(() => ({
  className: 'CenterNameEditImageButton',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 10;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    height: 24px;
    width: 24px;
  }
`;

export const EditActions = styled.div.attrs(() => ({
  className: 'CenterEditActions',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  ${({ theme }) => theme.breakPoints.tablet} {
    justify-content: flex-start;
  }
`;

export const EditCancelMobile = styled(Button).attrs(() => ({
  className: 'CenterEditCancelMobile',
}))`
  border: 1px solid ${({ theme }) => theme.colors.shadeMediumDark};
  color: ${({ theme }) => theme.colors.shadeMediumDark};

  height: 32px;
  width: 100%;
`;

export const EditCancel = styled(Cancel).attrs(() => ({
  className: 'CenterEditCancel',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  & .icon {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }
`;

export const EditSaveMobile = styled(Button).attrs(() => ({
  className: 'CenterEditSaveMobile',
}))`
  height: 32px;
  width: 100%;
`;

export const EditSaveDesktop = styled(Button).attrs(() => ({
  className: 'CenterEditSaveDesktop',
}))`
  padding: 0;
`;

export const EditSave = styled(Save).attrs(() => ({
  className: 'CenterEditSave',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;
`;
